import { Dialog, DialogContent, DialogTitle, IconButton, Theme, Typography, makeStyles } from "@material-ui/core"
import { MaterialSymbol } from "./MaterialSymbol"
import { HourReportingRolesTranslations } from "../hooks/use-translations"
import type { MemberSummary } from "../../../../memberships/api/hour-reporter-membership"
import { ReporterDetails } from "../types"
import { useTranslation } from "react-i18next"

type RoleDetailsDialogProps = ReporterDetails & {
  open: boolean
  member: MemberSummary | null
  translations: HourReportingRolesTranslations
  supportsChecking: boolean
  onClose: () => void
}

type ReporterSectionProps = {
  title: string
  reporters: MemberSummary[]
  emptyText: string
}

export const RoleDetailsDialog = ({
  open,
  member,
  checkedReporters,
  approvedReporters,
  translations,
  supportsChecking,
  onClose,
}: RoleDetailsDialogProps) => {
  const classes = useStyles()
  const memberName = member?.fullname ?? ""
  const { t: tOrg } = useTranslation("org")
  const roleDetailsDescription = tOrg("orgMembershipsPage.hourReportingRoles.roleDetailsDescriptionText", {
    defaultValue: "These members' reported hours are checked or approved by {{memberName}}.",
    memberName,
  })
  const checkedReportersTitle = tOrg("orgMembershipsPage.hourReportingRoles.checkedReportersHeaderText", {
    defaultValue: "Members whose hour reports {{memberName}} checks",
    memberName,
  })
  const approvedReportersTitle = tOrg("orgMembershipsPage.hourReportingRoles.approvedReportersHeaderText", {
    defaultValue: "Members whose hour reports {{memberName}} approves",
    memberName,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div className={classes.dialogHeaderText}>
          <Typography variant="h5">{translations.roleDetailsTitleText}</Typography>
          <Typography variant="body2" color="textSecondary" className={classes.dialogDescription}>
            {roleDetailsDescription}
          </Typography>
        </div>
        <IconButton size="small" onClick={onClose} aria-label={translations.closeDialogLabel}>
          <MaterialSymbol name="close" className={classes.dialogCloseIcon} />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {supportsChecking && (
          <ReporterSection
            title={checkedReportersTitle}
            reporters={checkedReporters}
            emptyText={translations.noCheckedReportersText}
          />
        )}
        <ReporterSection
          title={approvedReportersTitle}
          reporters={approvedReporters}
          emptyText={translations.noApprovedReportersText}
        />
      </DialogContent>
    </Dialog>
  )
}

const ReporterSection = ({ title, reporters, emptyText }: ReporterSectionProps) => {
  const classes = useStyles()

  return (
    <section className={classes.reporterSection}>
      <Typography variant="subtitle2" className={classes.reporterSectionHeader}>
        {title}
      </Typography>
      {reporters.length ? (
        <div className={classes.reporterList}>
          {reporters.map((reporter) => (
            <div key={reporter.id} className={classes.reporterItem}>
              <Typography variant="body2" className={classes.reporterName}>
                {reporter.fullname}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {reporter.userEmail}
              </Typography>
            </div>
          ))}
        </div>
      ) : (
        <Typography variant="body2" color="textSecondary" className={classes.emptyReporterText}>
          {emptyText}
        </Typography>
      )}
    </section>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    alignItems: "flex-start",
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2, 1),
  },
  dialogCloseIcon: {
    fontSize: 20,
    fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20",
  },
  dialogContent: {
    padding: theme.spacing(2, 2, 3),
  },
  dialogHeaderText: {
    paddingRight: theme.spacing(2),
  },
  dialogDescription: {
    marginTop: theme.spacing(0.75),
  },
  reporterSection: {
    "& + &": {
      marginTop: theme.spacing(4),
    },
  },
  reporterSectionHeader: {
    marginBottom: theme.spacing(1.25),
  },
  reporterList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
  },
  reporterItem: {
    padding: theme.spacing(1.25, 1.5),
    "& + &": {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  emptyReporterText: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.25, 1.5),
  },
  reporterName: {
    fontWeight: 600,
  },
}))
